import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Configuration,
  Flag,
  CardSection,
  SupplierClassification,
  DataRequest,
  DocumentRequest,
  Card,
  DataRequestCreate,
  DocumentRequestCreate,
  CardCreate
} from '../../_models/configuration.model';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private apiUrl = `${environment.urlApi}/configurations`;

  // Subject para configuración seleccionada
  private selectedConfigurationSubject = new BehaviorSubject<Configuration | null>(null);
  selectedConfiguration$ = this.selectedConfigurationSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Obtener todas las configuraciones
   */
  getAll(): Observable<Configuration[]> {
    return this.http.get<Configuration[]>(this.apiUrl).pipe(
      map((data) => data),
      catchError(this.handleError)
    );
  }

  /**
   * Obtener configuración por ID
   */
  getById(id: string): Observable<Configuration> {
    return this.http.get<Configuration>(`${this.apiUrl}/${id}`).pipe(
      map((data) => data),
      catchError(this.handleError)
    );
  }

  /**
   * Obtener configuración por companyId
   */
  getByCompanyId(companyId: string): Observable<Configuration> {
    return this.http.get<Configuration>(`${this.apiUrl}/company/${companyId}`).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Obtener la primera configuración disponible
   * Útil cuando solo hay una configuración por empresa
   */
  getConfiguration(): Observable<Configuration> {
    return this.getAll().pipe(
      map(configs => configs && configs.length > 0 ? configs[0] : null as any),
      catchError(this.handleError)
    );
  }

  /**
   * Crear nueva configuración
   */
  create(config: Configuration): Observable<Configuration> {
    return this.http.post<Configuration>(this.apiUrl, config).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Agregar una bandera a la configuración
   * @param configId ID de la configuración
   * @param flag Datos de la bandera (sin ID, el backend lo genera)
   * @param existingFlags Lista actual de banderas
   */
  addFlag(configId: string, flag: Partial<Flag>, existingFlags: Flag[]): Observable<Configuration> {
    const newFlag: Flag = {
      flagId: `class_${Date.now()}`,
      flagName: flag.flagName!,
      flagDescription: flag.flagDescription!,
      flagColor: flag.flagColor!,
      enabled:true
    };

    const updatedFlags = [...existingFlags, newFlag];

    const updatePayload = {
      general: {
        flags: updatedFlags
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar una bandera de la configuración
   * @param configId ID de la configuración
   * @param flagIndex Índice de la bandera a eliminar
   * @param existingFlags Lista actual de banderas
   */
  removeFlag(configId: string, flagIndex: number, existingFlags: Flag[]): Observable<Configuration> {
    const updatedFlags = existingFlags.filter((_, index) => index !== flagIndex);

    const updatePayload = {
      general: {
        flags: updatedFlags
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Agregar una sección a la configuración
   * @param configId ID de la configuración
   * @param section Datos de la sección (sin ID, el backend lo genera)
   * @param existingSections Lista actual de secciones
   */
  addCardSection(configId: string, section: Partial<CardSection>, existingSections: CardSection[]): Observable<Configuration> {
    const newSection : CardSection = {
      sectionId: `class_${Date.now()}`,
      sectionName: section.sectionName!,
      sectionDescription: section.sectionDescription!,
      enabled: true
    };

    const updatedSections = [...existingSections, newSection];

    const updatePayload = {
      general: {
        cardSections: updatedSections
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar una sección de la configuración
   * @param configId ID de la configuración
   * @param sectionIndex Índice de la sección a eliminar
   * @param existingSections Lista actual de secciones
   */
  removeCardSection(configId: string, sectionIndex: number, existingSections: CardSection[]): Observable<Configuration> {
    const updatedSections = existingSections.filter((_, index) => index !== sectionIndex);

    const updatePayload = {
      general: {
        cardSections: updatedSections
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar el email de notificaciones
   * @param configId ID de la configuración
   * @param email Email para notificaciones
   */
  updateMailNotifications(configId: string, email: string): Observable<Configuration> {
    const updatePayload = {
      general: {
        notificationMail: email
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar configuración con payload personalizado
   * @param configId ID de la configuración
   * @param updatePayload Objeto con los campos a actualizar
   */
  updateConfiguration(configId: string, updatePayload: any): Observable<Configuration> {
    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar el estado de una bandera
   * @param configId ID de la configuración
   * @param flagId ID de la bandera
   * @param enabled Estado de la bandera
   * @param existingFlags Lista actual de banderas
   */
  updateFlagStatus(configId: string, flagId: string, enabled: boolean, existingFlags: Flag[]): Observable<Configuration> {
    const updatedFlags = existingFlags.map(flag =>
      flag.flagId === flagId ? { ...flag, enabled } : flag
    );

    const updatePayload = {
      general: {
        flags: updatedFlags
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar el estado de una sección
   * @param configId ID de la configuración
   * @param sectionId ID de la sección
   * @param enabled Estado de la sección
   * @param existingSections Lista actual de secciones
   */
  updateSectionStatus(configId: string, sectionId: string, enabled: boolean, existingSections: CardSection[]): Observable<Configuration> {
    const updatedSections = existingSections.map(section =>
      section.sectionId === sectionId ? { ...section, enabled } : section
    );

    const updatePayload = {
      general: {
        cardSections: updatedSections
      }
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  // ========== MÉTODOS PARA DATOS SOLICITADOS (REQUESTED DATA) ==========

  /**
   * Agregar un campo solicitado a la configuración
   */
  addDataRequest(configId: string, dataRequest: DataRequestCreate, existingData: DataRequest[]): Observable<Configuration> {
    const newData = {
      fieldId: `campo_${Date.now()}`, // Generar ID único
      ...dataRequest
    };

    const updatedData = [...existingData, newData];

    const updatePayload = {
      dataRequest: updatedData
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar un campo solicitado
   */
  updateDataRequest(configId: string, fieldId: string, updatedData: Partial<DataRequest>, existingData: DataRequest[]): Observable<Configuration> {
    const updatedDataList = existingData.map(data =>
      data.fieldId === fieldId ? { ...data, ...updatedData } : data
    );

    const updatePayload = {
      dataRequest: updatedDataList
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un campo solicitado
   */
  removeDataRequest(configId: string, fieldId: string, existingData: DataRequest[]): Observable<Configuration> {
    const updatedData = existingData.filter(data => data.fieldId !== fieldId);

    const updatePayload = {
      dataRequest: updatedData
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  // ========== MÉTODOS PARA DOCUMENTOS SOLICITADOS (DOCUMENT REQUIREMENTS) ==========

  /**
   * Agregar un documento solicitado a la configuración
   */
  addDocumentRequest(configId: string, documentRequest: DocumentRequestCreate, existingDocs: DocumentRequest[]): Observable<Configuration> {
    const newDoc = {
      requestedDocId: `doc_${Date.now()}`, // Generar ID único
      ...documentRequest
    };

    const updatedDocs = [...existingDocs, newDoc];

    const updatePayload = {
      documentRequest: updatedDocs
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar un documento solicitado
   */
  updateDocumentRequest(configId: string, requestedDocId: string, updatedDoc: Partial<DocumentRequest>, existingDocs: DocumentRequest[]): Observable<Configuration> {
    const updatedDocsList = existingDocs.map(doc =>
      doc.requestedDocId === requestedDocId ? { ...doc, ...updatedDoc } : doc
    );

    const updatePayload = {
      documentRequest: updatedDocsList
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un documento solicitado
   */
  removeDocumentRequest(configId: string, requestedDocId: string, existingDocs: DocumentRequest[]): Observable<Configuration> {
    const updatedDocs = existingDocs.filter(doc => doc.requestedDocId !== requestedDocId);

    const updatePayload = {
      documentRequest: updatedDocs
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  // ========== MÉTODOS PARA TIPOS DE FICHA (TYPE SHEETS) ==========

  /**
   * Agregar un tipo de ficha a la configuración
   */
  addCard(configId: string, card: CardCreate, existingCards: Card[]): Observable<Configuration> {
    const newCard = {
      cardId: `ficha_${Date.now()}`, // Generar ID único
      ...card
    };

    const updatedCards = [...existingCards, newCard];

    const updatePayload = {
      cards: updatedCards
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar un tipo de ficha
   */
  updateCard(configId: string, cardId: string, updatedCard: Partial<Card>, existingCards: Card[]): Observable<Configuration> {
    const updatedCardsList = existingCards.map(card =>
      card.cardId === cardId ? { ...card, ...updatedCard } : card
    );

    const updatePayload = {
      cards: updatedCardsList
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar un tipo de ficha
   */
  removeCard(configId: string, cardId: string, existingCards: Card[]): Observable<Configuration> {
    const updatedCards = existingCards.filter(card => card.cardId !== cardId);

    const updatePayload = {
      cards: updatedCards
    };

    return this.http.patch<Configuration>(`${this.apiUrl}/${configId}`, updatePayload).pipe(
      map((data) => {
        if(data) this.setSelectedConfiguration(data);
        return data;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Eliminar configuración por ID
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Establecer la configuración seleccionada
   */
  setSelectedConfiguration(configuration: Configuration): void {
    this.selectedConfigurationSubject.next(configuration);
  }

  /**
   * Obtener la configuración seleccionada
   */
  getSelectedConfiguration(): Observable<Configuration | null> {
    return this.selectedConfiguration$;
  }

  /**
   * Limpiar la configuración seleccionada
   */
  clearSelectedConfiguration(): void {
    this.selectedConfigurationSubject.next(null);
  }

  /**
   * Manejo centralizado de errores
   */
  private handleError(error: any): Observable<never> {
    console.error('Configuration Service Error:', error);
    return throwError(
      () => new Error(error.error?.message || 'An error occurred, please try again later.')
    );
  }
}